import { useState, useCallback, useEffect, useRef } from "react";

const COPY_TIMEOUT = 3000

const useClipboard = () => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef(null)

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  // Bug real (revisión hce-summary 2026-08-11): sin cleanup, un unmount
  // dentro de la ventana de 3s (ej. HceSummary.js, que ahora se desmonta
  // más seguido — cada cambio de paciente o cierre del panel, por el fix
  // de key={consumerID} de otra pasada de esta misma revisión) dispara
  // setCopied sobre una instancia ya desmontada.
  //
  // `html` es OPCIONAL (QA-Cormos, card Resumen HCE con IA, 24/09): pegar
  // el resumen copiado en otro campo mostraba el markdown crudo ("##
  // Antecedentes...") en vez de negrita/subrayado, porque writeText() solo
  // pone texto plano en el portapapeles. Con `html`, se escribe TAMBIÉN
  // como text/html (ClipboardItem) — el destino que sepa pegar rich text
  // (Word, Gmail, la mayoría de los editores) muestra el formato real; el
  // texto plano sigue yendo de fallback para el resto. Sin `html` (el resto
  // de los call sites de este hook: links de pago, etc.) el comportamiento
  // es EXACTAMENTE el de antes — writeText liso, sin tocar nada.
  //
  // Bug real (CI de PR #369, build de producción): `ClipboardItem` sin
  // calificar es un global que el ESLint de CRA no reconoce (no-undef) —
  // Jest lo dejaba pasar porque no corre lint, pero `yarn build` sí lo
  // exige y rompía el build. `window.ClipboardItem` es la misma referencia
  // en un browser real (y en jsdom, que aliasea window a global) sin
  // pelearse con el linter.
  //
  // El intento de rich-copy en sí puede FALLAR en runtime aunque el
  // browser lo soporte (ej. permisos, algunas versiones de Safari son
  // más estrictas con Blob+ClipboardItem) — antes de este fix, un fallo
  // ahí tiraba directo al catch de abajo y la copia se perdía ENTERA, algo
  // peor que el writeText simple de siempre. Ahora ese intento tiene su
  // propio try/catch con fallback a writeText — nunca queda peor que antes.
  const copyToClipboard = useCallback(async (text, html) => {
    try {
      let usedRichCopy = false
      if (html && navigator.clipboard.write && typeof window.ClipboardItem !== 'undefined') {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({
              'text/plain': new Blob([text], { type: 'text/plain' }),
              'text/html': new Blob([html], { type: 'text/html' }),
            }),
          ])
          usedRichCopy = true
        } catch (richErr) {
          console.error('Error al copiar como rich text, cayendo a texto plano:', richErr)
        }
      }
      if (!usedRichCopy) {
        await navigator.clipboard.writeText(text)
      }
      setCopied(true)
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopied(false), COPY_TIMEOUT)
    } catch (err) {
      console.error('Error al copiar:', err)
    }
  }, [])

  return { copied, copyToClipboard }
}

export default useClipboard
